import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

// Legacy Finance drew the accruing period as one "Upcoming" row at the top
// of the payout list: Sent and Arrive by both read "-", and the amount was
// everything Stripe had already settled into the account but had not yet
// swept into a payout. In Connect that same money is `available + pending`
// on the balance `connectApi.toConnectBalance` hands back.
//
// Round 2 (spec 2026-09-22 §F): Stripe now groups that money by the day it
// expects to sweep it into a payout (`balance.upcomingGroups`, soonest
// first), so this draws one row per group, latest first — a real, if
// estimated, arrival date — instead of the single dateless row the first
// round printed.
// The dateless row is not gone: it is the fallback for a balance the API has
// not started grouping yet (`upcomingGroups` empty or absent) that still
// carries money, so an older API response or a mid-migration window keeps
// showing exactly what the pre-groups contract drew.
//
// A payout Stripe has already created — however it currently reads in the
// list (Pending, In transit, whatever) — is a real row already, so this
// helper never duplicates it: it is strictly the money still sitting in the
// account, not yet swept into any payout object.
//
// Every row carries the same `stripePayoutId` (spec 2026-09-22 §13): there
// is one Upcoming detail page, reading Connect/Finance/UpcomingDetail for
// all of this money together, not one page per arrival date.
export const UPCOMING_PAYOUT_ID = 'upcoming';

const rowOf = (balance, { id, amount, arrivalDate, shortfall = false }) => ({
  id,
  stripePayoutId: UPCOMING_PAYOUT_ID,
  amount,
  arrivalDate,
  destinationBankName: balance.bankName || null,
  destinationLast4: balance.bankLast4 || null,
  shortfall,
});

// Bugfix 2026-09-23: every Upcoming row used to open the same detail page —
// all pending money, regardless of which arrival-date group was clicked
// (spec §13 said one page for all of it; the owner report is that this reads
// as a bug once the list draws several dated rows side by side). Each row
// now opens its own group by carrying its arrival date on the URL; a row
// with no date (the pre-groups fallback) still opens the bare id.
export const upcomingPayoutPath = (row) => {
  const arrivalDate = row?.arrivalDate;
  return arrivalDate
    ? `${UPCOMING_PAYOUT_ID}?arrival=${dayjs.utc(arrivalDate).format('YYYY-MM-DD')}`
    : UPCOMING_PAYOUT_ID;
};

export const upcomingPayouts = (balance) => {
  if (!balance) return [];
  const all = Array.isArray(balance.upcomingGroups) ? balance.upcomingGroups : [];
  const groupRow = (group, shortfall) =>
    rowOf(balance, { id: `upcoming-${group.arrivalDate}`, amount: group.amount, arrivalDate: group.arrivalDate, shortfall });

  // A group below zero is no payout: it is money Stripe takes from the next
  // sales (owner report 2026-09-23: Starbucks read "Upcoming -$1.26" as if it
  // were one). It is still listed, flagged, because it is the only way to
  // open the order and the refund that made it (owner, same day). It
  // promises nothing, so a manual schedule lists it too. Exactly zero is
  // nothing either way and is not drawn.
  const shortfalls = all.filter((group) => group.amount < 0).map((group) => groupRow(group, true));

  // Manual: nothing arrives by itself. The hero's schedule rail already
  // says Manual, and the Pay out button is the way money moves — a row
  // promising an "Upcoming" payout here would be a promise we cannot keep.
  if (balance.scheduleInterval === 'manual') return shortfalls;

  // Latest date first (owner report 2026-09-24): the payouts drawn below
  // these rows run newest first, and the API sends groups soonest first, so
  // the list read Sep 28, Oct 5, then Sep 23 — two directions in one list.
  // Sorted on a copy: the hero's Next payout reads the soonest group from
  // the balance's own list.
  const groups = all
    .filter((group) => group.amount > 0)
    .sort((a, b) => dayjs.utc(b.arrivalDate).valueOf() - dayjs.utc(a.arrivalDate).valueOf());
  if (groups.length > 0 || shortfalls.length > 0) {
    return [...groups.map((group) => groupRow(group, false)), ...shortfalls];
  }

  const amount = (balance.available || 0) + (balance.pending || 0);
  if (amount <= 0) return [];
  return [rowOf(balance, { id: 'upcoming', amount, arrivalDate: null })];
};
